import {
  Box,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Search, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import amazonLogo from "../../common/assets/img/platform_logos/amazon.svg";
import appleAppStoreLogo from "../../common/assets/img/platform_logos/apple_app_store.png";
import audibleLogo from "../../common/assets/img/platform_logos/audible.svg";
import googleAdsLogo from "../../common/assets/img/platform_logos/google_ads.webp";
import googlePlayLogo from "../../common/assets/img/platform_logos/google_play.svg";
import patreonLogo from "../../common/assets/img/platform_logos/patreon.webp";
import spotifyLogo from "../../common/assets/img/platform_logos/spotify.svg";
import temuLogo from "../../common/assets/img/platform_logos/temu.svg";
import Button from "../../common/components/Button";
import { SPACING } from "../../common/constants/spacing";
import { LinkFacebookCard } from "./LinkFacebookCard";
import { LinkInstagramCard } from "./LinkInstagramCard";
import { LinkKickCard } from "./LinkKickCard";
import { LinkTikTokCard } from "./LinkTikTokCard";
import { LinkTwitchCard } from "./LinkTwitchCard";
import { LinkVimeoCard } from "./LinkVimeoCard";
import { LinkYoutubeCard } from "./LinkYoutubeCard";

export const AddSourceModal = ({ open, onClose, createSource }) => {
  const { t } = useTranslation("sources");
  const [searchTerm, setSearchTerm] = useState("");

  // Define all sources with their data
  const allSources = [
    {
      name: "YouTube",
      component: <LinkYoutubeCard createSource={createSource} />,
      isCustomComponent: true,
    },
    {
      name: "Google Ads",
      logo: googleAdsLogo,
      description: t("platforms.googleAds.description"),
      buttonText: t("platforms.googleAds.buttonText"),
      isCustomComponent: false,
    },
    {
      name: "Amazon",
      logo: amazonLogo,
      description: t("platforms.amazon.description"),
      buttonText: t("platforms.amazon.buttonText"),
      isCustomComponent: false,
    },
    {
      name: "TikTok",
      component: <LinkTikTokCard createSource={createSource} />,
      isCustomComponent: true,
    },
    {
      name: "Vimeo",
      component: <LinkVimeoCard createSource={createSource} />,
      isCustomComponent: true,
    },
    {
      name: "Instagram",
      component: <LinkInstagramCard createSource={createSource} />,
      isCustomComponent: true,
    },
    {
      name: "Twitch",
      component: <LinkTwitchCard createSource={createSource} />,
      isCustomComponent: true,
    },
    {
      name: "Kick",
      component: <LinkKickCard createSource={createSource} />,
      isCustomComponent: true,
    },
    {
      name: "Patreon",
      logo: patreonLogo,
      description: t("platforms.patreon.description"),
      buttonText: t("platforms.patreon.buttonText"),
      isCustomComponent: false,
    },
    {
      name: "Facebook",
      component: <LinkFacebookCard createSource={createSource} />,
      isCustomComponent: true,
    },
    {
      name: "Apple App Store",
      logo: appleAppStoreLogo,
      description: t("platforms.appleAppStore.description"),
      buttonText: t("platforms.appleAppStore.buttonText"),
      isCustomComponent: false,
    },
    {
      name: "Google Play",
      logo: googlePlayLogo,
      description: t("platforms.googlePlay.description"),
      buttonText: t("platforms.googlePlay.buttonText"),
      isCustomComponent: false,
    },
    {
      name: "Audible",
      logo: audibleLogo,
      description: t("platforms.audible.description"),
      buttonText: t("platforms.audible.buttonText"),
      isCustomComponent: false,
    },
    {
      name: "Spotify",
      logo: spotifyLogo,
      description: t("platforms.spotify.description"),
      buttonText: t("platforms.spotify.buttonText"),
      isCustomComponent: false,
    },
    {
      name: "Temu",
      logo: temuLogo,
      description: t("platforms.temu.description"),
      buttonText: t("platforms.temu.buttonText"),
      isCustomComponent: false,
    },
  ];

  // Filter sources based on search term and sort by integration status
  const filteredSources = allSources
    .filter((source) =>
      source.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      // Put integrated sources (isCustomComponent: true) first
      if (a.isCustomComponent && !b.isCustomComponent) return -1;
      if (!a.isCustomComponent && b.isCustomComponent) return 1;
      return 0; // Keep original order for sources with same status
    });

  const renderSourceCard = (source) => {
    if (source.isCustomComponent) {
      return source.component;
    }

    return (
      <Grid size={{ xs: 12, md: 4 }} key={source.name}>
        <Card
          variant="outlined"
          sx={{
            p: 3,
            height: "100%",
            mt: 1,
            opacity: 0.7,
            position: "relative",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        >
          {/* Coming Soon Badge */}
          <Chip
            label={t("addSourceModal.comingSoon")}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: "primary.main",
              color: "white",
              fontSize: "0.75rem",
              fontWeight: "bold",
              zIndex: 1,
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            <Box>
              {source.isSvgComponent ? (
                <source.logo
                  style={{
                    height: "70px",
                    width: "auto",
                    marginBottom: 10,
                  }}
                />
              ) : (
                <img
                  src={source.logo}
                  alt={`${source.name} Logo`}
                  style={{
                    height: "70px",
                    objectFit: "contain",
                    marginBottom: 10,
                  }}
                />
              )}
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", mb: SPACING.xs }}
              >
                {source.name}
              </Typography>
              <Typography variant="bodySm" sx={{ color: "text.secondary" }}>
                {source.description}
              </Typography>
            </Box>

            <Button variant="outlined" sx={{ mt: 3 }} disabled fullWidth>
              {source.buttonText}
            </Button>
          </Box>
        </Card>
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
        }}
      >
        <Typography variant="h2" component="span">
          {t("addSourceModal.title")}
        </Typography>
        <IconButton onClick={onClose}>
          <X size={25} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        {/* Search Input */}
        <Box sx={{ mt: 3, mb: 3, display: "flex", justifyContent: "flex-end" }}>
          <TextField
            placeholder={t("addSourceModal.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              width: "300px",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#6b7280" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Sources Grid */}
        <Grid container spacing={3}>
          {filteredSources.map((source) => renderSourceCard(source))}
        </Grid>

        {/* No results message */}
        {filteredSources.length === 0 && searchTerm && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {t("addSourceModal.noResults", { searchTerm })}
            </Typography>
            <Typography variant="bodySm" color="text.secondary" sx={{ mt: 1 }}>
              {t("addSourceModal.noResultsHint")}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
